import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Printer, Home, ArrowLeft } from "lucide-react";
import useAuth from "../../contexts/AuthContext";
import Loader from "../../common_components/Loader";
import { REACT_APP_LOGO_URL } from "../../constants";

interface FormField {
  id: string;
  type: string;
  label: string;
  fields?: FormField[];
}

interface FormSection {
  id: string;
  title: string;
  fields: FormField[];
}

interface ApplicationSchema {
  sections?: FormSection[];
}

interface Recruitment {
  title: string;
  applicationFormSchema?: ApplicationSchema;
}

interface DocumentEntry {
  url?: string;
}

interface TotalExperience {
  years: number;
  months: number;
  days: number;
}

interface FormData {
  documents?: Record<string, DocumentEntry>;
  totalExperience?: TotalExperience;
  [key: string]: unknown;
}

interface Payment {
  amount?: number;
  utrNumber?: string;
  paymentDate?: string;
  status?: string;
}

interface Declaration {
  acceptedAt?: string;
}

interface Application {
  formData: FormData;
  payment?: Payment;
  declaration?: Declaration;
  status: string;
  submittedAt?: string;
  documents?: Record<string, DocumentEntry>;
}

export default function PrintApplication() {
  const { recruitmentId, applicationId } = useParams<{
    recruitmentId: string;
    applicationId: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { apiRequest } = useAuth();

  const isNonFaculty = location.pathname.includes("non-faculty");
  const apiPrefix = isNonFaculty ? "/non-faculty" : "/faculty";

  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<Application | null>(null);
  const [recruitment, setRecruitment] = useState<Recruitment | null>(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [appRes, recRes] = await Promise.all([
          apiRequest(`${apiPrefix}/application/${applicationId}`, { method: "GET" }),
          apiRequest(`${apiPrefix}/recruitment/${recruitmentId}`, { method: "GET" }),
        ]);

        if (appRes?.application) setApplication(appRes.application);
        if (recRes?.recruitment) setRecruitment(recRes.recruitment);
      } catch (err) {
        console.error("Print load failed:", err);
      } finally {
        setLoading(false);
      }
    }

    if (applicationId && recruitmentId) {
      loadData();
    }
  }, [apiPrefix, apiRequest, applicationId, recruitmentId]);

  // Auto trigger print when loaded
  useEffect(() => {
    if (!loading && application && recruitment) {
      const timer = setTimeout(() => {
        window.print();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loading, application, recruitment]);

  if (loading) return <Loader />;
  if (!application || !recruitment) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-bold text-red-600">Failed to load application data.</h2>
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">
          Go Back
        </button>
      </div>
    );
  }

  const { formData, payment, declaration, status, submittedAt } = application;
  const sections = recruitment.applicationFormSchema?.sections || [];

  const getCandidatePhotoUrl = (): string | null => {
    return application.documents?.photo?.url || formData.documents?.photo?.url || null;
  };

  const getCandidateSigUrl = (): string | null => {
    return application.documents?.signature?.url || formData.documents?.signature?.url || null;
  };

  const formatValue = (field: FormField, val: unknown): string => {
    if (val === undefined || val === null || val === "") return "N/A";

    if (field.type === "checkbox") {
      return val ? "Yes" : "No";
    }

    if (field.type === "date") {
      return new Date(val as string).toLocaleDateString("en-IN");
    }

    if (field.type === "address") {
      const addr = val as Record<string, string>;
      return `${addr.addressLine || ""}, ${addr.city || ""}, ${addr.state || ""} - ${addr.pincode || ""}`;
    }

    if (field.type === "file" || field.type === "image") {
      return (val as { key?: string }).key ? "Document Uploaded" : "N/A";
    }

    return String(val);
  };

  const docFieldsMap: Record<string, string> = {
    photo: "Passport Photo",
    signature: "Candidate Signature",
    allCert: "All Educational & Experience Certificates",
    paymentCert: "Payment Proof / Receipt",
  };

  sections.forEach((sec) => {
    (sec.fields || []).forEach((f) => {
      if (f.type === "file" || f.type === "image" || sec.id === "documents") {
        if (!docFieldsMap[f.id]) {
          docFieldsMap[f.id] = f.label || f.id;
        }
      }
    });
  });

  const rawDocs = {
    ...(formData.documents || {}),
    ...(application.documents || {}),
  };

  const allDocKeys = Array.from(
    new Set([...Object.keys(docFieldsMap), ...Object.keys(rawDocs)])
  );

  return (
    <div className="min-h-screen bg-white p-4 font-sans text-slate-900 printable-application print:min-h-0 print:p-0">
      {/* Controls Bar - Hidden during printing */}
      <div className="max-w-4xl mx-auto mb-6 flex justify-between items-center border-b pb-4 print:hidden">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-lg text-xs font-semibold"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-xs font-semibold"
          >
            <Printer className="w-4 h-4" /> Print Now
          </button>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-lg text-xs font-semibold"
          >
            <Home className="w-4 h-4" /> Go Home
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto border border-gray-300 p-6 space-y-6 print:border-0 print:p-0 print:space-y-4">
        {/* Header Block */}
        <div className="text-center border-b-2 border-slate-900 pb-4 relative pt-2 print:pb-3">
          {/* AIIMS Logo - Top Left Corner */}
          <div className="absolute top-0 left-0 w-24 h-24 flex items-center justify-center print:w-20 print:h-20">
            <img
              src={REACT_APP_LOGO_URL}
              alt="AIIMS Logo"
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = "https://via.placeholder.com/150?text=AIIMS";
              }}
            />
          </div>

          <div className="px-28 print:px-24">
            <h1 className="text-xl font-black uppercase tracking-tight text-slate-900 print:text-lg">
              ALL INDIA INSTITUTE OF MEDICAL SCIENCES, MANGALAGIRI
            </h1>
            <p className="text-sm font-bold text-slate-700 mt-1 uppercase print:text-xs">{recruitment.title}</p>
          </div>

          <div className="grid grid-cols-2 gap-4 text-left text-xs mt-4 pt-3 border-t print:mt-2 print:pt-2">
            <div>
              <p>Application ID: <strong>{applicationId}</strong></p>
              <p>Status: <strong className="uppercase">{status}</strong></p>
              {submittedAt && (
                <p>Submitted On: <strong>{new Date(submittedAt).toLocaleString("en-IN")}</strong></p>
              )}
              <p>Print Date: <strong>{new Date().toLocaleDateString("en-IN")}</strong></p>
            </div>
          </div>

          {/* Inline Photo rendering */}
          <div className="absolute top-0 right-0 w-28 h-32 border-2 border-slate-900 bg-slate-50 flex items-center justify-center overflow-hidden print:w-24 print:h-28">
            {getCandidatePhotoUrl() ? (
              <img src={getCandidatePhotoUrl() || ""} alt="Candidate Headshot" className="w-full h-full object-cover" />
            ) : (
              <span className="text-[10px] text-gray-400 text-center font-bold">No Photo</span>
            )}
          </div>
        </div>

        {/* Dynamic Form Sections */}
        <div className="space-y-6 pt-4 print:space-y-4 print:pt-2">
          {sections.map((section) => {
            const textFields = section.fields.filter(
              (f) => f.type !== "file" && f.type !== "image" && f.type !== "repeater"
            );
            const repeaters = section.fields.filter((f) => f.type === "repeater");

            const sectionData = (formData[section.id] || {}) as Record<string, unknown>;

            return (
              <div key={section.id} className="space-y-3 print:space-y-2">
                <h2 className="text-sm font-extrabold uppercase bg-slate-100 text-slate-900 px-3 py-1.5 border-l-4 border-slate-900 print:break-after-avoid">
                  {section.title}
                </h2>

                {section.id === "experience" && formData.totalExperience && (
                  <div className="bg-slate-50 border border-gray-300 rounded p-3 text-xs mb-3 flex items-center justify-between print:py-2 print:break-inside-avoid">
                    <div>
                      <span className="font-bold text-slate-700 block">Calculated Total Work Experience:</span>
                      <span className="text-slate-900 font-semibold mt-0.5 block">
                        {formData.totalExperience.years} Years, {formData.totalExperience.months} Months,{" "}
                        {formData.totalExperience.days} Days
                      </span>
                    </div>
                  </div>
                )}

                {textFields.length > 0 && (
                  <table className="w-full text-xs border border-gray-300 border-collapse">
                    <tbody>
                      {textFields.map((field) => (
                        <tr key={field.id} className="border-b border-gray-300 print:break-inside-avoid">
                          <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                            {field.label}
                          </td>
                          <td className="py-2 px-3 text-slate-900 print:py-1">
                            {formatValue(field, sectionData[field.id])}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Repeaters */}
                {repeaters.map((field) => {
                  const items = (sectionData[field.id] || []) as Record<string, unknown>[];
                  return (
                    <div key={field.id} className="space-y-2 pt-2 print:space-y-1 print:pt-1">
                      <span className="block text-xs font-bold text-slate-700 print:break-after-avoid">{field.label}</span>
                      {items.length === 0 ? (
                        <p className="text-xs text-gray-500 italic px-3">No entries added.</p>
                      ) : (
                        <table className="w-full text-xs border border-gray-300 border-collapse">
                          <thead>
                            <tr className="bg-slate-50 border-b border-gray-300 text-slate-700 print:break-inside-avoid">
                              {field.fields?.map((sf) => (
                                <th key={sf.id} className="py-1.5 px-2 border-r border-gray-300 text-left font-bold print:py-1">
                                  {sf.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, idx) => (
                              <tr key={idx} className="border-b border-gray-300 print:break-inside-avoid">
                                {field.fields?.map((sf) => (
                                  <td
                                    key={sf.id}
                                    className="py-1.5 px-2 border-r border-gray-300 text-left font-normal text-slate-900 print:py-1"
                                  >
                                    {formatValue(sf, item[sf.id])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Uploaded Documents Summary */}
        <div className="space-y-3 print:space-y-2">
          <h2 className="text-sm font-extrabold uppercase bg-slate-100 text-slate-900 px-3 py-1.5 border-l-4 border-slate-900 print:break-after-avoid">
            Uploaded Documents Summary
          </h2>
          <table className="w-full text-xs border border-gray-300 border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-gray-300 text-slate-700 print:break-inside-avoid">
                <th className="py-1.5 px-3 border-r border-gray-300 text-left font-bold w-1/3 print:py-1">
                  Document Name
                </th>
                <th className="py-1.5 px-3 border-r border-gray-300 text-left font-bold print:py-1">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {allDocKeys.map((key) => {
                const docEntry = rawDocs[key as keyof typeof rawDocs] as DocumentEntry | undefined;
                const isUploaded = !!docEntry?.url;
                const label =
                  docFieldsMap[key] ||
                  key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

                if (!isUploaded && !docFieldsMap[key]) return null;

                return (
                  <tr key={key} className="border-b border-gray-300 print:break-inside-avoid">
                    <td className="py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">
                      {label}
                    </td>
                    <td className="py-2 px-3 text-slate-900 print:py-1">
                      {isUploaded ? (
                        <span className="text-green-700 font-semibold">Uploaded</span>
                      ) : (
                        <span className="text-gray-400 italic">Not Uploaded</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Offline Payment Information */}
        {payment?.amount && payment.amount > 0 && (
          <div className="space-y-3 print:space-y-2">
            <h2 className="text-sm font-extrabold uppercase bg-slate-100 text-slate-900 px-3 py-1.5 border-l-4 border-slate-900 print:break-after-avoid">
              Payment Information
            </h2>
            <table className="w-full text-xs border border-gray-300 border-collapse">
              <tbody>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">Amount</td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">₹{payment.amount}</td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">UTR / Reference ID</td>
                  <td className="py-2 px-3 text-slate-900 font-mono print:py-1">{payment.utrNumber || "N/A"}</td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">Payment Date</td>
                  <td className="py-2 px-3 text-slate-900 print:py-1">
                    {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString("en-IN") : "N/A"}
                  </td>
                </tr>
                <tr className="border-b border-gray-300 print:break-inside-avoid">
                  <td className="w-1/3 py-2 px-3 font-bold bg-slate-50 border-r border-gray-300 text-slate-700 print:py-1">Payment Status</td>
                  <td className="py-2 px-3 text-slate-900 font-bold uppercase print:py-1">{payment.status}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Candidate Signature & Declaration */}
        <div className="space-y-4 pt-4 print:space-y-3 print:pt-2 print:break-inside-avoid">
          <div className="p-4 bg-slate-50 border border-gray-300 rounded text-xs space-y-2 print:p-2.5 print:space-y-1">
            <span className="block font-bold text-slate-800 uppercase">Declaration Checklist Status: ACCEPTED</span>
            <p className="text-slate-600 leading-relaxed text-[10px]">
              The candidate accepted the following terms: "I hereby declare that the above information is true, complete and correct.
              I understand that my candidature is liable to be rejected in the event of any misstatement detected before or after appointment."
            </p>
            {declaration?.acceptedAt && (
              <p className="text-[10px] text-gray-500 font-bold">
                Digitally accepted on: {new Date(declaration.acceptedAt).toLocaleString("en-IN")}
              </p>
            )}
          </div>

          <div className="flex justify-between items-end pt-6 print:pt-4">
            <div className="flex flex-col items-center">
              {getCandidateSigUrl() ? (
                <div className="w-40 h-16 border border-gray-300 p-1 flex items-center justify-center overflow-hidden mb-2 print:w-36 print:h-14">
                  <img src={getCandidateSigUrl() || ""} alt="Candidate Signature" className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="w-40 h-12 border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400 mb-2 print:w-36 print:h-10">
                  No Signature Uploaded
                </div>
              )}
              <span className="text-[10px] font-bold text-slate-700">CANDIDATE SIGNATURE</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
